import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import RoundCount from 'Components/RoundCount';

import { useShouldDisableJSRouting } from '@/components/providers/DisableJSRoutingProvider';
import { cn } from '@/lib/utils';

/**
 * Renders a navigation item component.
 *
 * @param {object} props - The properties for the navigation item.
 * @param {string} [props.url] - The URL for the navigation item.
 * @param {string} props.text - The text for the navigation item.
 * @param {import('@fortawesome/fontawesome-svg-core').IconDefinition} [props.icon] - The icon for the navigation item.
 * @param {import('preact/compat').ReactElement} [props.customIcon] - The custom icon for the navigation item.
 * @param {function} [props.onClick] - The click event handler for the navigation item.
 * @param {number} [props.count=0] - The count for the navigation item.
 * @param {string} [props.target] - The target for the navigation item.
 * @param {boolean} [props.avoidInnerPadding] - Removes the padding on the inner element.
 * @param {string} [props.className] - The class name for the navigation item.
 * @return {import('preact/compat').ReactElement} The rendered navigation item component.
 */
const NavItem = ({ url, text, icon, customIcon, onClick, count = 0, target, avoidInnerPadding, className, ...props }) => (
  <div
    className={clsx('group text-sm font-medium text-stone-500 hover:text-stone-900', !avoidInnerPadding && 'px-4 pt-3')}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  >
    <a
      data-native={useShouldDisableJSRouting()}
      className={cn('preflight preflight-a relative flex items-center gap-1', className)}
      href={url}
      onClick={onClick}
      target={target}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <span className={clsx('flex', !avoidInnerPadding && 'mr-1 w-5')}>
        {customIcon || (icon && <FontAwesomeIcon icon={icon} className="text-stone-500 group-hover:text-stone-900" />)}
      </span>
      {count > 0 && <RoundCount count={count} />}
      <span>{text}</span>
    </a>
  </div>
);

export default NavItem;
